import React, { useState, useEffect } from "react";
import { T } from "./theme";
import { SectionHeader, Card, Input, Sel, Btn, Badge, Modal, Note, Avatar } from "./ui";
import { Icon } from "./Icons";
import { MODULES } from "./data";
import { supabase, isSupabaseReady } from "./supabase";

// ═══════════════════════════════════════════════════════════════════════
// USUÁRIOS (admin) — gestão de acessos com permissões granulares por módulo
// ═══════════════════════════════════════════════════════════════════════

const ROLES = [
  { id: "proprietario",  label: "Proprietário",        cor: T.gold,  desc: "Dono da operação — acesso total e irrestrito" },
  { id: "admin_master",  label: "Admin Master",        cor: T.gold,  desc: "Acesso elevado — pode editar comissões e regalias, igual ao proprietário" },
  { id: "admin",         label: "Admin",               cor: T.err,   desc: "Acesso completo aos módulos, mas SEM poder editar valores de comissão" },
  { id: "comercial",     label: "Comercial",           cor: T.brand, desc: "Acesso de admin (confirma/edita vendas), MAS continua aparecendo no ranking (vende e pontua)" },
  { id: "gestor",        label: "Gestor",              cor: T.brand, desc: "Acesso a relatórios e gestão operacional" },
  { id: "logistica",     label: "Logística",           cor: T.info,  desc: "Encarregado de logística e pós-venda" },
  { id: "operacional",   label: "Operacional",         cor: T.info,  desc: "Suporte interno e ferramentas operacionais" },
];

export default function Usuarios({ users, setUsers, corretores, currentUser }) {
  const meuUsername = currentUser?.username;
  function podeEditar(u) {
    if (u.role !== "proprietario") return true;
    return meuUsername && u.username === meuUsername;
  }
  const [busca, setBusca] = useState("");
  const [editando, setEditando] = useState(null);
  const [novo, setNovo] = useState(false);

  // Corretores NÃO são usuários do sistema. Filtra.
  const usuariosSistema = (users || []).filter(u => u.role !== "corretor");

  const filtrados = usuariosSistema.filter(u =>
    !busca || u.nome.toLowerCase().includes(busca.toLowerCase()) || u.username.toLowerCase().includes(busca.toLowerCase())
  );

  // Carrega usuários do Supabase ao montar (sobrescreve localStorage)
  useEffect(() => {
    if (!isSupabaseReady) return;
    (async () => {
      const { data } = await supabase.from("usuarios").select("*").order("id");
      if (data && data.length) {
        const norm = data.map(u => ({
          id: u.id,
          username: u.username,
          email: u.email || "",
          nome: u.nome,
          password: u.password,
          role: u.role,
          corretorId: u.corretor_id || null,
          modules: Array.isArray(u.modules) ? u.modules : (u.modules ? JSON.parse(u.modules) : []),
        }));
        setUsers(norm);
      }
    })();
  }, []);

  async function salvar(u) {
    if (!isSupabaseReady) {
      // fallback localStorage
      if (u.id) setUsers(users.map(x => x.id === u.id ? u : x));
      else setUsers([...users, { ...u, id: Date.now() }]);
      setEditando(null); setNovo(false);
      return;
    }
    // Se marcou "criar novo corretor", cria o cadastro primeiro e usa o UUID
    if (u._novoCorretor) {
      try {
        const nc = u._novoCorretor;
        const base = (u.nome || "corretor").trim().split(/\s+/)[0].toLowerCase().replace(/[^a-z0-9]/g, "") || "corretor";
        const tag = base + "-" + Date.now().toString(36).slice(-4);
        const ehAdminRole = u.role === "admin" || u.role === "admin_master" || u.role === "proprietario";
        const { data: cor, error: ec } = await supabase.from("corretores").insert({
          nome: u.nome,
          email: u.email ? u.email.trim().toLowerCase() : null,
          telefone: (nc.telefone || "").replace(/\D/g, ""),
          cidade: nc.cidade || null,
          uf: nc.uf ? nc.uf.toUpperCase() : null,
          tipo: ehAdminRole ? u.role : "corretor", // admin também-corretor NÃO entra no ranking
          ativo: true,
          codigo_indicacao: tag,
          tag_unica: tag,
        }).select("id").single();
        if (ec) throw ec;
        u = { ...u, corretorId: cor.id };
      } catch (e) { alert("Erro ao criar corretor: " + e.message); return; }
    }
    try {
      const payload = {
        username: u.username.trim().toLowerCase(),
        email: u.email ? u.email.trim().toLowerCase() : null,
        nome: u.nome,
        password: u.password,
        role: u.role,
        corretor_id: u.corretorId || null,
        modules: u.modules || [],
      };
      if (u.id && Number.isInteger(u.id) && users.find(x => x.id === u.id && x._fromDb !== false)) {
        // UPDATE existente
        const { error } = await supabase.from("usuarios").update(payload).eq("id", u.id);
        if (error) throw error;
        setUsers(users.map(x => x.id === u.id ? { ...x, ...u } : x));
      } else {
        // Se já existe um usuário pra essa pessoa (mesmo corretor vinculado, mesmo username
        // ou mesmo email), ATUALIZA em vez de duplicar → vira o login com o novo papel.
        let existente = null;
        const orF = [];
        if (u.corretorId) orF.push(`corretor_id.eq.${u.corretorId}`);
        if (payload.username) orF.push(`username.eq.${payload.username}`);
        if (payload.email) orF.push(`email.eq.${payload.email}`);
        if (orF.length) {
          const { data: ex } = await supabase.from("usuarios").select("id").or(orF.join(",")).limit(1);
          existente = ex && ex[0];
        }
        if (existente) {
          const { error } = await supabase.from("usuarios").update(payload).eq("id", existente.id);
          if (error) throw error;
          setUsers(prev => [...prev.filter(x => x.id !== existente.id), { ...u, id: existente.id }]);
        } else {
          // INSERT novo
          const { data, error } = await supabase.from("usuarios").insert(payload).select().single();
          if (error) throw error;
          setUsers([...users, { ...u, id: data.id }]);
        }
      }
      // Sincroniza o tipo do corretor com o role: admin também-corretor sai do ranking
      if (u.corretorId) {
        const corTipo = (u.role === "admin" || u.role === "admin_master" || u.role === "proprietario") ? u.role : "corretor";
        try { await supabase.from("corretores").update({ tipo: corTipo }).eq("id", u.corretorId); } catch (_) {}
      }
      setEditando(null); setNovo(false);
    } catch (e) {
      alert("Erro ao salvar: " + e.message);
    }
  }

  async function excluir(id) {
    if (!window.confirm("Remover este usuário? Ele não poderá mais acessar o app.")) return;
    if (!isSupabaseReady) {
      setUsers(users.filter(u => u.id !== id));
      return;
    }
    try {
      const { error } = await supabase.from("usuarios").delete().eq("id", id);
      if (error) throw error;
      setUsers(users.filter(u => u.id !== id));
    } catch (e) {
      alert("Erro ao remover: " + e.message);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <SectionHeader
        title="Usuários do Sistema"
        subtitle={`${usuariosSistema.length} usuários do sistema · proprietário, admin, gestores e equipe interna`}
        icon={<Icon.lock size={24} />}
        right={<Btn onClick={() => setNovo(true)} icon={<Icon.plus size={14} />}>Novo Usuário</Btn>}
      />

      <Note tone="info" icon={<Icon.info size={14} />}>
        Cada usuário recebe um papel (admin/corretor/operacional) e uma lista de módulos que pode acessar. Admin tem acesso a tudo por padrão. Para corretores, o módulo "Ranking" é essencial para que vejam seu perfil.
      </Note>

      <Input placeholder="Buscar por nome ou usuário..." value={busca} onChange={e => setBusca(e.target.value)} />

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {filtrados.map(u => {
          const role = ROLES.find(r => r.id === u.role);
          const initials = u.nome.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
          return (
            <Card key={u.id} style={{ padding: 16 }}>
              <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                <Avatar initials={initials} size={40} tone={role.cor} />
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 15, fontWeight: 500, color: T.text }}>{u.nome}</div>
                    <Badge text={role.label.toUpperCase()} tone={(u.role === "admin" || u.role === "proprietario") ? "err" : u.role === "corretor" ? "brand" : "info"} />
                  </div>
                  <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>
                    @{u.username}{u.email ? ` · ${u.email}` : ""} · {u.modules.filter(m => MODULES.find(mm => mm.id === m)).length} de {MODULES.length} módulos liberados
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {podeEditar(u) ? (
                    <Btn variant="ghost" icon={<Icon.edit size={12} />} onClick={() => setEditando(u)}>Editar</Btn>
                  ) : (
                    <span style={{ fontSize: 10, color: T.textDim, fontStyle: "italic" }}>🔒 Protegido</span>
                  )}
                  {u.role !== "admin" && u.role !== "proprietario" && (
                    <Btn variant="danger" icon={<Icon.trash size={12} />} onClick={() => excluir(u.id)}>Remover</Btn>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {(editando || novo) && (
        <UsuarioForm
          user={editando || { username: "", email: "", password: "", nome: "", role: "corretor", corretorId: null, modules: ["ranking","regulamento"] }}
          corretores={corretores}
          onSave={salvar}
          onCancel={() => { setEditando(null); setNovo(false); }}
        />
      )}
    </div>
  );
}

function UsuarioForm({ user, onSave, onCancel, corretores }) {
  const [u, setU] = useState(user);
  const [ehCorretor, setEhCorretor] = useState(!!user.corretorId);
  const [corretorNovo, setCorretorNovo] = useState({ cidade: "", uf: "", telefone: "" });

  function salvarForm() {
    let payload = { ...u };
    if (!ehCorretor) {
      payload.corretorId = null; payload._novoCorretor = null;
    } else {
      if (u.corretorId === "__novo__") {
        if (!corretorNovo.cidade || !corretorNovo.uf) { alert("Preencha cidade e UF do novo corretor."); return; }
        payload.corretorId = null; payload._novoCorretor = { ...corretorNovo };
      }
      // Garante acesso à config de corretor + ranking (essenciais pro corretor)
      const mods = new Set(payload.modules || []);
      mods.add("meusettings"); mods.add("ranking");
      payload.modules = Array.from(mods);
    }
    onSave(payload);
  }

  function toggle(mod) {
    const has = u.modules.includes(mod);
    setU({ ...u, modules: has ? u.modules.filter(x => x !== mod) : [...u.modules, mod] });
  }

  function selecionarTodos() { setU({ ...u, modules: MODULES.map(m => m.id) }); }
  function limparTodos() { setU({ ...u, modules: ["regulamento"] }); }

  return (
    <Modal title={user.id ? "Editar Usuário" : "Novo Usuário"} onClose={onCancel} width={580}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="Nome completo" value={u.nome} onChange={e => setU({ ...u, nome: e.target.value })} />
        <Input label="Usuário (username)" value={u.username} onChange={e => setU({ ...u, username: e.target.value })} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Input label="Email (login alternativo)" type="email" value={u.email || ""} onChange={e => setU({ ...u, email: e.target.value })} />
        <Sel label="Papel" value={u.role} onChange={e => setU({ ...u, role: e.target.value })}>
          {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
        </Sel>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
        <Input label="Senha" value={u.password} onChange={e => setU({ ...u, password: e.target.value })} />
      </div>

      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <label style={{ fontSize: 11, color: T.textSub, fontWeight: 500, letterSpacing: 0.8, textTransform: "uppercase" }}>
            Módulos liberados ({u.modules.filter(m => MODULES.find(mm => mm.id === m)).length}/{MODULES.length})
          </label>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={selecionarTodos} style={{ background: "none", border: "none", fontSize: 11, color: T.brand, cursor: "pointer", fontWeight: 500 }}>Todos</button>
            <button onClick={limparTodos} style={{ background: "none", border: "none", fontSize: 11, color: T.textSub, cursor: "pointer", fontWeight: 500 }}>Limpar</button>
          </div>
        </div>

        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6,
          background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md,
          padding: 10, maxHeight: 280, overflowY: "auto",
        }}>
          {MODULES.map(mod => {
            const active = u.modules.includes(mod.id);
            return (
              <label key={mod.id} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "6px 10px", borderRadius: T.r.sm, cursor: "pointer",
                background: active ? T.brandSoft : "transparent",
                transition: `all 120ms ${T.ease}`,
              }}>
                <input type="checkbox" checked={active} onChange={() => toggle(mod.id)} style={{ cursor: "pointer", accentColor: T.brand }} />
                <span style={{ fontSize: 12, color: active ? T.brand : T.text, fontWeight: active ? 500 : 400 }}>{mod.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 4, padding: "10px 12px", background: T.input, border: `1px solid ${T.border}`, borderRadius: T.r.md }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: T.text }}>
          <input
            type="checkbox"
            checked={ehCorretor}
            onChange={e => { setEhCorretor(e.target.checked); if (!e.target.checked) setU({ ...u, corretorId: null }); }}
            style={{ accentColor: T.brand, cursor: "pointer" }}
          />
          Este usuário <b>também é corretor</b> (recebe comissão; só aparece no ranking se o cargo for "corretor")
        </label>
        {ehCorretor && (
          <div style={{ marginTop: 10 }}>
            <Sel
              label="Corretor(a) vinculado(a)"
              value={u.corretorId || ""}
              onChange={e => setU({ ...u, corretorId: e.target.value || null })}
            >
              <option value="">— selecione —</option>
              <option value="__novo__">➕ Criar novo corretor (a partir deste usuário)</option>
              {(corretores || []).map(c => (
                <option key={c._uuid || c.id} value={c._uuid}>{c.nome} · {c.cidade}-{c.uf}</option>
              ))}
            </Sel>
            {u.corretorId === "__novo__" && (
              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "2fr 1fr 2fr", gap: 8 }}>
                <Input label="Cidade" value={corretorNovo.cidade} onChange={e => setCorretorNovo({ ...corretorNovo, cidade: e.target.value })} />
                <Input label="UF" value={corretorNovo.uf} onChange={e => setCorretorNovo({ ...corretorNovo, uf: e.target.value.toUpperCase().slice(0, 2) })} placeholder="CE" />
                <Input label="Telefone / WhatsApp" value={corretorNovo.telefone} onChange={e => setCorretorNovo({ ...corretorNovo, telefone: e.target.value })} placeholder="85999999999" />
              </div>
            )}
            <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>
              Vincule a um corretor existente ou crie um novo. Ele ganha acesso às configurações de corretor + comissão + ranking.
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onCancel}>Cancelar</Btn>
        <Btn onClick={salvarForm} icon={<Icon.check size={14} />}>Salvar</Btn>
      </div>
    </Modal>
  );
}
